import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, Check, Trash2 } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "./ui/dropdown-menu";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export function timeAgo(iso, t) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return t("notif.just_now");
  if (s < 3600) return t("notif.m_ago", { n: Math.floor(s / 60) });
  if (s < 86400) return t("notif.h_ago", { n: Math.floor(s / 3600) });
  return t("notif.d_ago", { n: Math.floor(s / 86400) });
}

// Header notification bell with an unread badge + recent-items dropdown (LinkedIn-style).
export default function NotificationBell() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const timer = useRef(null);

  const loadCount = () => api.get("/me/notifications/unread-count").then(({ data }) => setUnread(data.count)).catch(() => {});
  const loadList = () => api.get("/me/notifications?limit=8").then(({ data }) => { setItems(data.items); setUnread(data.unread); }).catch(() => {});

  useEffect(() => {
    if (!user) return undefined;
    loadCount();
    timer.current = setInterval(loadCount, 60000); // poll once a minute
    return () => clearInterval(timer.current);
  }, [user]);

  if (!user) return null;

  const open = (n) => {
    if (!n.read) api.post(`/me/notifications/${n.notification_id}/read`).catch(() => {});
    navigate(n.link || "/dashboard");
  };
  const markRead = (e, n) => { e.stopPropagation(); api.post(`/me/notifications/${n.notification_id}/read`).then(loadList).catch(() => {}); };
  const remove = (e, n) => {
    e.stopPropagation();
    setItems((p) => p.filter((x) => x.notification_id !== n.notification_id));
    api.delete(`/me/notifications/${n.notification_id}`).then(loadList).catch(() => {});
  };
  const clearAll = () => { setItems([]); setUnread(0); api.delete("/me/notifications").then(loadList).catch(() => {}); };

  return (
    <DropdownMenu onOpenChange={(o) => o && loadList()}>
      <DropdownMenuTrigger asChild>
        <button className="relative inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card hover:border-primary/40 transition-colors" data-testid="notif-bell" aria-label="Notifications">
          <Bell className="h-4 w-4" />
          {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center" data-testid="notif-badge">{unread > 9 ? "9+" : unread}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-[70vh] overflow-y-auto p-0">
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border">
          <span className="text-sm font-semibold">{t("notif.title")}</span>
          <div className="flex items-center gap-3">
            {items.length > 0 && <button className="text-xs text-muted-foreground hover:text-rose-600" onClick={clearAll} data-testid="notif-clear-all">{t("notif.clear_all")}</button>}
            <button className="text-xs text-[hsl(var(--primary))]" onClick={() => navigate("/dashboard?tab=notifications")} data-testid="notif-see-all">{t("notif.see_all")}</button>
          </div>
        </div>
        {items.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">{t("notif.empty")}</p>
        ) : items.map((n) => (
          <div key={n.notification_id} className={`group flex items-start gap-1.5 px-3 py-2.5 border-b border-border/60 hover:bg-secondary ${n.read ? "" : "bg-sky-50/60"}`} data-testid="notif-item">
            <button onClick={() => open(n)} className="flex items-start gap-2 text-left flex-1 min-w-0">
              {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-rose-500 shrink-0" />}
              <div className="min-w-0">
                <p className="text-sm font-medium leading-snug">{n.title}</p>
                {n.body && <p className="text-xs text-muted-foreground line-clamp-2">{n.body}</p>}
                <p className="text-[11px] text-muted-foreground mt-0.5">{timeAgo(n.created_at, t)}</p>
              </div>
            </button>
            <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              {!n.read && (
                <button onClick={(e) => markRead(e, n)} title={t("notif.mark_read")} aria-label={t("notif.mark_read")} className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-card" data-testid="notif-item-read">
                  <Check className="h-3.5 w-3.5" />
                </button>
              )}
              <button onClick={(e) => remove(e, n)} title={t("notif.delete")} aria-label={t("notif.delete")} className="p-1 rounded text-muted-foreground hover:text-rose-600 hover:bg-card" data-testid="notif-item-delete">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
